import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Checkbox } from '../../ui/checkbox';
import { Button } from '../../ui/button';
import { ArrowUp, ArrowDown, Group } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '../../ui/tooltip';
import type { SortField } from '@/types/base';

export interface ColumnDefinition {
  id: string;
  label: string;
  visible: boolean;
  width?: number;
  sortable?: boolean;
  filterable?: boolean;
}

export interface ColumnsRecord {
  [key: string]: ColumnDefinition;
}

interface TransactionTableHeaderProps {
  columnOrder: string[];
  transactionColumns: ColumnsRecord;
  columnWidths: Record<string, string>;
  selectedTransactions: Set<string>;
  totalTransactions: number;
  sortField: SortField;
  sortDirection: 'asc' | 'desc';
  isColumnSorting: boolean;
  groupBy: string | null;
  onToggleAllTransactions: () => void;
  onSort: (field: SortField) => void;
  onResizeStart: (e: React.MouseEvent<HTMLDivElement>, columnId: string) => void;
  onGroupBy: (field: string | null) => void;
}

export const TransactionTableHeader: React.FC<TransactionTableHeaderProps> = React.memo(({
  columnOrder,
  transactionColumns,
  columnWidths,
  selectedTransactions,
  totalTransactions,
  sortField,
  sortDirection,
  isColumnSorting,
  groupBy,
  onToggleAllTransactions,
  onSort,
  onResizeStart,
  onGroupBy
}) => {
  const { t } = useTranslation();

  /**
   * Render sort button with arrows - memoized to prevent infinite re-renders
   */
  const renderSortButton = useCallback((field: string, label: string) => {
    const sortFieldName = field as SortField;
    const isCurrentSortField = sortField === sortFieldName;
    const isUnsortable = ['tags', 'notes'].includes(field);
    
    const tooltipText = isUnsortable 
      ? `Cannot sort by ${label}` 
      : isCurrentSortField
        ? `Currently sorted by ${label} (${sortDirection === 'desc' ? 'newest first' : 'oldest first'}). Click to ${sortDirection === 'desc' ? 'sort oldest first' : 'sort newest first'}.`
        : `Sort by ${label}`;

    if (isUnsortable) {
      return <span className="font-medium text-sm text-gray-500">{label}</span>;
    }

    return (
      <div className="flex items-center gap-1.5 group">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                onClick={() => onSort(sortFieldName)}
                disabled={isColumnSorting}
                className={cn(
                  "h-7 px-2 flex items-center gap-1.5 rounded-md transition-colors",
                  isCurrentSortField 
                    ? "bg-primary/10 text-primary hover:bg-primary/15"
                    : "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800/70",
                  "font-medium text-sm"
                )}
              >
                {label}
                {isColumnSorting ? (
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary ml-1"></div>
                ) : (
                  <div className={cn(
                    "flex flex-col gap-0.5 transition-opacity",
                    isCurrentSortField ? "opacity-100" : "opacity-50 group-hover:opacity-80"
                  )}>
                    <ArrowUp className={cn(
                      "h-2.5 w-2.5 transition-colors",
                      isCurrentSortField && sortDirection === 'desc' 
                        ? "text-primary" 
                        : "text-gray-400"
                    )} />
                    <ArrowDown className={cn(
                      "h-2.5 w-2.5 transition-colors",
                      isCurrentSortField && sortDirection === 'asc' 
                        ? "text-primary" 
                        : "text-gray-400"
                    )} />
                  </div>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" align="center" className="text-xs">
              {tooltipText}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {!isUnsortable && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onGroupBy(groupBy === field ? null : field)}
                  className={cn(
                    "h-7 w-7 p-0 rounded-md transition-all",
                    groupBy === field 
                      ? "bg-primary/10 text-primary hover:bg-primary/15" 
                      : "text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800/70 dark:text-gray-400",
                    "opacity-0 group-hover:opacity-100 focus:opacity-100",
                    groupBy === field && "opacity-100"
                  )}
                >
                  <Group className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" align="center" className="text-xs">
                {groupBy === field ? `Remove ${label} grouping` : `Group by ${label}`}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    );
  }, [sortField, sortDirection, isColumnSorting, groupBy, onSort, onGroupBy]);

  return (
    <tr className="transactions-header-row">
      {/* Checkbox column */}
      <th className="checkbox-column">
        <Checkbox
          checked={selectedTransactions.size === totalTransactions && totalTransactions > 0}
          onCheckedChange={onToggleAllTransactions}
          className={cn(
            "transition-all duration-200 h-4 w-4",
            selectedTransactions.size > 0 
              ? "opacity-100" 
              : "opacity-70 hover:opacity-100",
          )}
          aria-label="Select all transactions"
        />
      </th>
      
      {/* Dynamic columns */}
      {columnOrder && columnOrder.length > 0 && columnOrder.map((columnId) => (
        transactionColumns && 
        transactionColumns[columnId] && 
        transactionColumns[columnId].visible && (
          <th 
            key={columnId}
            className="table-header-cell resizable-cell"
            style={{ 
              width: columnWidths[columnId],
              minWidth: columnWidths[columnId],
              maxWidth: columnWidths[columnId]
            }}
            data-column-id={columnId}
          >
            <div className="flex items-center">
              {renderSortButton(columnId as string, t(`transactions.columns.${columnId}`))}
            </div>
            <div 
              className="resizer"
              onMouseDown={(e) => onResizeStart(e, columnId)} 
              onTouchStart={(e) => {
                // Add touch support for mobile
                const touch = e.touches[0];
                const mouseEvent = new MouseEvent('mousedown', {
                  clientX: touch.clientX,
                  clientY: touch.clientY
                });
                e.target.dispatchEvent(mouseEvent);
                e.preventDefault();
              }}
              title={`Resize ${t(`transactions.columns.${columnId}`)} column`}
            />
          </th>
        )
      ))}
      
      {/* Actions column */}
      <th className="action-column">
        <span className="sr-only">Actions</span>
      </th>
    </tr>
  );
}); 